import React, { useCallback, useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Stack,
  InputAdornment,
  Divider,
  useTheme,
  Grid,
  Avatar,
  CircularProgress,
} from '@mui/material';
import {
  Instagram as InstagramIcon,
  YouTube as YouTubeIcon,
  Email as EmailIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { 
  EditProfilePaperContainer, 
  MainContentArea, 
  MainContentAreaChildGrid, 
  UploadBox 
} from './styled';
import { useNavigate } from 'react-router-dom';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import useCurrentUserInfo from '../../hooks/useCurrentUserInfo';
import TinyMceEditor from '../../components/TinyMceEditor';
import { useDispatch, useSelector } from 'react-redux';
import { updateChefBasicInfo } from '../../redux/actions/userActions';
import { urlHelpers } from './helpers';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { debounce } from 'lodash';
import { useDropzone } from 'react-dropzone';

function EditProfileInfo(){

  const theme = useTheme();
  const navigate = useNavigate();
  const userInfo = useCurrentUserInfo();
  const dispatch = useDispatch();

  const chefProfileInfo = useSelector((state) => state?.userData?.chefProfileInfo);
  
  const [bioContent, setBioContent] = useState(userInfo?.bioContent);
  const [avatar, setAvatar] = useState(userInfo?.chefProfileAvatar);
  const [formData, setFormData] = useState({
    instagramLink: chefProfileInfo?.instagramLink,
    youtubeLink: chefProfileInfo?.youtubeLink,
    email: chefProfileInfo?.email,
    username: chefProfileInfo?.username,
    userId: userInfo?.user_id,
    chefProfileAvatar: chefProfileInfo?.chefProfileAvatar
  });
  const [errors, setErrors] = useState({
    instagramLink: '',
    youtubeLink: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const debouncedValidation = useCallback(
    debounce((field, value) => {
      const helper = field === 'instagramLink' ? urlHelpers.instagram : urlHelpers.youtube;
      
      if (!value) {
        setErrors(prev => ({ ...prev, [field]: '' }));
        return;
      }
  
      if (!helper.validate(value)) {
        setErrors(prev => ({ ...prev, [field]: helper.errorMessage }));
      } else {
        setErrors(prev => ({ ...prev, [field]: '' }));
        // Format the URL if valid
        const formattedUrl = helper.format(value);
        setFormData(prev => ({ ...prev, [field]: formattedUrl }));
      }
    }, 300),
    []
  );

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormData({
      ...formData,
      [field]: value
    });

    if(field === 'youtubeLink' || field === 'instagramLink'){
      debouncedValidation(field, value)
    };
  };

  // const handleLinkChange = (index) => (event) => {
  //   const newLinks = [...formData.externalLinks];
  //   newLinks[index] = event.target.value;
  //   setFormData({
  //     ...formData,
  //     externalLinks: newLinks
  //   });
  // };

  // const clearLink = (index) => {
  //   const newLinks = [...formData.externalLinks];
  //   newLinks[index] = '';
  //   setFormData({
  //     ...formData,
  //     externalLinks: newLinks
  //   });
  // };

  const onDrop = useCallback(async (acceptedFiles) => {
    setError(null);
    const file = acceptedFiles[0];
    
    if (file) {
      setIsLoading(true);
      try {
        // Create preview
        const reader = new FileReader();
        reader.onloadend = () => {
          setAvatar(reader.result);
        };
        reader.readAsDataURL(file);

        // Here you would typically upload the file to your server
        // const formData = new FormData();
        // formData.append('avatar', file);
        // await uploadToServer(formData);

      } catch (err) {
        setError('Error uploading file');
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif']
    },
    maxSize: 5 * 1024 * 1024, // 5MB
    multiple: false,
    onDropRejected: (fileRejections) => {
      const error = fileRejections[0]?.errors[0];
      if (error.code === 'file-too-large') {
        setError('File is too large. Maximum size is 5MB');
      } else if (error.code === 'file-invalid-type') {
        setError('Please upload an image file');
      } else {
        setError('Invalid file');
      }
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const validationErrors = {};
    
    if (formData.instagramLink && !urlHelpers.instagram.validate(formData.instagramLink)) {
      validationErrors.instagramLink = urlHelpers.instagram.errorMessage;
    }
    
    if (formData.youtubeLink && !urlHelpers.youtube.validate(formData.youtubeLink)) {
      validationErrors.youtubeLink = urlHelpers.youtube.errorMessage;
    }
    
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    };
    
    dispatch(
      updateChefBasicInfo( {
        formData: {
          ...formData,
          bioContent: bioContent,
          instagramLink: urlHelpers.instagram.format(formData.instagramLink),
          youtubeLink: urlHelpers.youtube.format(formData.youtubeLink),
          chefProfileAvatar: avatar
        },
        bioContent
      }, dispatch )
    );
  };

  return (
    <MainContentArea elevation={0}>
      <MainContentAreaChildGrid container >
        <Grid
          sx={{ 
            backgroundColor: `${theme.palette.primary.main}`,
            height: '3rem',
            width: '100%',
            padding: '1rem 1rem',
            borderBottomLeftRadius: '2rem', 
            borderBottomRightRadius: '2rem',
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center'
          }}
        >
          <Button
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-around',
              width: '6rem'
            }}
            onClick={() => navigate(-1)}
            color='secondary'
          >
            <ArrowBackOutlinedIcon />
            <Typography variant='h7' color={'secondary'}>
                Back
              </Typography>
          </Button>
        </Grid>
        <Grid maxWidth="md" sx={{ maxWidth: 'none', width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
          <EditProfilePaperContainer elevation={3}>
            <Typography variant="h4" gutterBottom sx={{ mb: 4 }}>
              Edit Profile
            </Typography>

            <form onSubmit={handleSubmit}>
              <Stack spacing={3}>

              <Box>
                <Typography variant="h6" gutterBottom color="primary" sx={{ marginBottom: '1rem' }}>
                  Chef Profile Avatar
                </Typography>
                <Box 
                  display="flex" 
                  gap={4} 
                  sx={{ 
                    flexDirection: { xs: 'column', md: 'row' },
                    alignItems: { xs: 'center', md: 'flex-start' }
                  }}
                >
                  {/* Current Avatar Display */}
                  <Box 
                    flex={1} 
                    display="flex" 
                    flexDirection="column" 
                    alignItems="center"
                    sx={{ 
                      minWidth: { md: '200px' },
                      maxWidth: { md: '300px' }
                    }}
                  >
                    <Avatar
                      src={avatar || '/default-avatar.png'}
                      sx={{ 
                        width: 200, 
                        height: 200,
                        border: '2px solid',
                        borderColor: 'primary.main'
                      }}
                    />
                  </Box>

                  {/* Upload Section */}
                  <Box flex={1}>
                    <Stack spacing={2}>
                      <UploadBox
                        isDragActive={isDragActive}
                        {...getRootProps()}
                        sx={{ 
                          width: '100%',
                          minHeight: '200px',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center'
                        }}
                      >
                        <input {...getInputProps()} />
                        {isLoading ? (
                          <CircularProgress />
                        ) : (
                          <Grid>
                            <CloudUploadIcon color="primary" sx={{ fontSize: 48, marginBottom: 2 }} />
                            <Typography variant="body1" gutterBottom>
                              {isDragActive
                                ? "Drop the image here"
                                : "Drag and drop your image here or click to select"}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              Supported formats: JPG, PNG, GIF (max 5MB)
                            </Typography>
                          </Grid>
                        )}
                      </UploadBox>
                      
                      {error && (
                        <Typography color="error" variant="body2">
                          {error}
                        </Typography>
                      )}
                    </Stack>
                  </Box>
                </Box>
              </Box>
                <Divider />

                {/* Bio Section */}
                <Box>
                  <Typography variant="h6" gutterBottom color="primary" sx={{ marginBottom: '1rem' }}>
                    Chef Bio
                  </Typography>
                  <Stack spacing={2}>
                    <TinyMceEditor content={bioContent} setContent={setBioContent}/>
                  </Stack>
                </Box>

                <Divider />

                {/* Social Media Section */}
                <Box>
                  <Typography variant="h6" gutterBottom color="primary" sx={{ marginBottom: '1rem' }}>
                    Social Media
                  </Typography>
                  <Stack spacing={2}>
                    <TextField
                      fullWidth
                      label="Instagram URL"
                      value={formData.instagramLink}
                      onChange={handleChange('instagramLink')}
                      error={!!errors.instagramLink}
                      helperText={errors.instagramLink}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <InstagramIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                      placeholder="https://instagram.com/username"
                    />

                    <TextField
                      fullWidth
                      label="YouTube URL"
                      value={formData.youtubeLink}
                      onChange={handleChange('youtubeLink')}
                      error={!!errors.youtubeLink}
                      helperText={errors.youtubeLink}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <YouTubeIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                      placeholder="https://youtube.com/channel/..."
                    />
                  </Stack>
                </Box>

                <Divider />

                {/* Account Information Section */}
                <Box>
                  <Typography variant="h6" gutterBottom color="primary" sx={{ marginBottom: '1rem' }}>
                    Account Information
                  </Typography>
                  <Stack spacing={2}>
                    <TextField
                      fullWidth
                      label="Email Address"
                      type="email"
                      value={formData.email}
                      onChange={handleChange('email')}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />

                    <TextField
                      fullWidth
                      label="Username"
                      value={formData.username}
                      onChange={handleChange('username')}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Stack>
                </Box>

                <Divider />

                {/* External Links Section */}
                {/* <Box>
                  <Typography variant="h6" gutterBottom color="primary" sx={{ marginBottom: '1rem' }}>
                    External Links
                  </Typography>
                  <Stack spacing={2}>
                    {formData.externalLinks.map((link, index) => (
                      <TextField
                        key={index}
                        fullWidth
                        label={`External Link ${index + 1}`}
                        value={link}
                        onChange={handleLinkChange(index)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <LinkIcon color="action" />
                            </InputAdornment>
                          ),
                          endAdornment: link && (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => clearLink(index)}
                                edge="end"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    ))}
                  </Stack>
                </Box> */}

                {/* Submit Button */}
                <Box sx={{ display: 'flex', gap: 2, pt: 2, width: '100%' }}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    type="submit"
                    sx={{ flex: 1 }}
                  >
                    Save Changes
                  </Button>
                </Box>
              </Stack>
            </form>
          </EditProfilePaperContainer>
        </Grid>
      </MainContentAreaChildGrid>
    </MainContentArea>
  );
};

export default EditProfileInfo;